import { DOTS_PER_MM } from "@hisense/shared";

/**
 * Đọc lại luồng lệnh TSPL để VẼ LẠI tem trên màn hình.
 *
 * Vì sao phải đọc lại lệnh thay vì dựng bản xem trước từ dữ liệu gốc: bản xem
 * trước dựng từ dữ liệu chỉ chứng minh dữ liệu đúng, còn cái in ra sai hay đúng
 * là do LỆNH. Toạ độ, cắt chữ, chỗ giữ cho mã vạch, dòng bị bỏ vì hết chỗ —
 * tất cả nằm trong chuỗi gửi cho máy. Vẽ từ chính chuỗi đó thì nhân viên nhìn
 * thấy đúng thứ máy sẽ in, kể cả khi bộ dựng lệnh có lỗi.
 *
 * CHƯA ĐỐI CHIẾU VỚI MÁY THẬT: chưa có CLabel CT321D để so. Kích thước ký tự
 * lấy theo đặc tả font dựng sẵn của TSPL, không phải đo từ bản in thật — nên
 * đây là mô phỏng trung thực nhất có thể làm được mà không có máy, không phải
 * bằng chứng đã in đúng.
 */

export interface TsplTextOp {
  type: "text";
  x: number; y: number;
  font: string; rotation: number; xMul: number; yMul: number;
  text: string;
}
export interface TsplBarcodeOp {
  type: "barcode";
  x: number; y: number;
  codeType: string; height: number; readable: boolean; rotation: number;
  narrow: number; wide: number;
  text: string;
}
export type TsplOp = TsplTextOp | TsplBarcodeOp;
export interface TsplLabel {
  widthMm: number; heightMm: number;
  widthDots: number; heightDots: number;
  copies: number;
  ops: TsplOp[];
}

/**
 * Ô ký tự (dot) của font dựng sẵn trong máy, theo đặc tả TSPL.
 *
 * Bộ dựng lệnh dùng ước lượng RỘNG HƠN để cắt chữ cho chắc ăn (label.ts,
 * CHAR_W). Ở đây phải dùng số của đặc tả: mục đích của bản xem trước là cho
 * thấy máy in ra sao, không phải nhắc lại giả định của bộ dựng lệnh.
 */
const FONT_CELL: Record<string, { w: number; h: number }> = {
  "1": { w: 8, h: 12 }, "2": { w: 12, h: 20 }, "3": { w: 16, h: 24 },
  "4": { w: 24, h: 32 }, "5": { w: 32, h: 48 },
};
export const DEFAULT_CELL = FONT_CELL["1"]!;
export function fontCell(font: string): { w: number; h: number } {
  return FONT_CELL[font] ?? DEFAULT_CELL;
}

const num = (v: string | undefined) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** `TEXT x,y,"font",rot,xmul,ymul,"nội dung"` */
const TEXT_RE = /^TEXT\s+(-?\d+)\s*,\s*(-?\d+)\s*,\s*"([^"]*)"\s*,\s*(-?\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*"(.*)"\s*$/i;
/** `BARCODE x,y,"loại",cao,đọc được,xoay,hẹp,rộng,"nội dung"` */
const BARCODE_RE = /^BARCODE\s+(-?\d+)\s*,\s*(-?\d+)\s*,\s*"([^"]*)"\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*(-?\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*"(.*)"\s*$/i;
const SIZE_RE = /^SIZE\s+([\d.]+)\s*mm\s*,\s*([\d.]+)\s*mm\s*$/i;
const PRINT_RE = /^PRINT\s+(\d+)/i;

/**
 * Trả về `null` khi luồng lệnh không khai khổ tem: không có khổ thì không biết
 * tờ giấy to bằng nào, và vẽ đại một khung tự chọn là nói dối người xem về chỗ
 * còn trống trên tem.
 */
export function parseTspl(payload: string): TsplLabel | null {
  let widthMm = 0, heightMm = 0, copies = 1;
  let ops: TsplOp[] = [];
  for (const raw of payload.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const size = SIZE_RE.exec(line);
    if (size) { widthMm = num(size[1]); heightMm = num(size[2]); continue; }
    if (/^CLS$/i.test(line)) { ops = []; continue; }
    const print = PRINT_RE.exec(line);
    if (print) { copies = Math.max(1, num(print[1])); continue; }
    const text = TEXT_RE.exec(line);
    if (text) {
      ops.push({ type: "text", x: num(text[1]), y: num(text[2]), font: text[3]!, rotation: num(text[4]), xMul: Math.max(1, num(text[5])), yMul: Math.max(1, num(text[6])), text: text[7]! });
      continue;
    }
    const barcode = BARCODE_RE.exec(line);
    if (barcode) {
      ops.push({ type: "barcode", x: num(barcode[1]), y: num(barcode[2]), codeType: barcode[3]!, height: num(barcode[4]), readable: num(barcode[5]) > 0, rotation: num(barcode[6]), narrow: Math.max(1, num(barcode[7])), wide: Math.max(1, num(barcode[8])), text: barcode[9]! });
      continue;
    }
    // GAP / DIRECTION / REFERENCE và mọi lệnh khác không vẽ gì lên tem.
  }
  if (!(widthMm > 0 && heightMm > 0)) return null;
  return { widthMm, heightMm, widthDots: Math.round(widthMm * DOTS_PER_MM), heightDots: Math.round(heightMm * DOTS_PER_MM), copies, ops };
}
